/**
 * taipeiDateLabels — the short date / relative-day / notes-stamp labels of 課務總覽
 * (Hub UI redesign W0.12). Pure: every function takes `nowMs` from the caller (its
 * debug-aware getNow()), so a list renders from one clock and the tests pin it.
 *
 * Every count is Taipei CALENDAR days (FrontendConfigUtils.taipeiCalendarDaysUntil),
 * never 24-hour blocks and never the browser's zone — a row created yesterday 18:00
 * and viewed today 09:00 is 「1 天前」 on every machine.
 *
 * Replaces the three hand-rolled copies (TransferRequestsTab / TransferAdminTable /
 * ProgramWaitlistTab relativeAgeLabel, which said 「昨天」 — the redesign writes
 * 「1 天前」) as those tabs are rebuilt. Re-exported by useStandardizedTimestamps.
 */
import {
  FRONTEND_BUSINESS_RULES,
  FrontendConfigUtils,
  SESSION_ENROLLMENT_CONFIG,
} from '@/config/appConfig';

const DAY_MS = 24 * 60 * 60 * 1000;
/** Asia/Taipei is UTC+8 all year (no DST) — the same fixed offset the backend uses. */
export const TAIPEI_OFFSET_MS = 8 * 60 * 60 * 1000;

/** Taipei calendar days from `ms` to `nowMs` (positive = in the past). */
export function taipeiDaysSince(ms: number, nowMs: number): number {
  return FrontendConfigUtils.taipeiCalendarDaysUntil(nowMs, ms);
}

/** 今天 / N 天前 / N 天後, counted in Taipei calendar days. */
export function formatRelativeDays(ms: number, nowMs: number): string {
  const days = taipeiDaysSince(ms, nowMs);
  if (days === 0) return '今天';
  return days > 0 ? `${days} 天前` : `${-days} 天後`;
}

/** Taipei weekday of an instant, 0 = Sunday (the epoch 1970-01-01 was a Thursday). */
export function taipeiWeekdayOf(ms: number): number {
  const taipeiDay = Math.floor((ms + TAIPEI_OFFSET_MS) / DAY_MS);
  return (((taipeiDay + 4) % 7) + 7) % 7;
}

/** Taipei calendar date of an instant: [year, month 1–12, day]. */
function taipeiYmd(ms: number): [number, number, number] {
  const d = new Date(ms + TAIPEI_OFFSET_MS);
  return [d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate()];
}

/**
 * 「9/27 (日)」 in Taipei — with the year (「2027/1/9 (六)」) when the date falls in
 * another Taipei year than `nowMs`, like formatShortTaipeiDate (a list sorted by date
 * across a new year must not read out of order). The existing formatToDayOfWeek reads
 * the browser's local calendar and must not be used for these lists.
 */
export function formatShortDateWithWeekday(ms: number, nowMs: number): string {
  const weekdayName = FRONTEND_BUSINESS_RULES.DATE_TIME.WEEKDAY_NAMES[taipeiWeekdayOf(ms)] ?? '';
  // WEEKDAY_NAMES are 「週日」…「週六」; the bracket carries the single character.
  const short = weekdayName.slice(-1);
  return `${formatShortDate(ms, nowMs)} (${short})`;
}

/** 「9/27」 in Taipei — 「2027/1/9」 when it falls in another Taipei year than `nowMs`. */
export function formatShortDate(ms: number, nowMs: number): string {
  const [y, m, d] = taipeiYmd(ms);
  const [nowY] = taipeiYmd(nowMs);
  const md = `${m}/${d}`;
  return y === nowY ? md : `${y}/${md}`;
}

export interface NoteStamp {
  /** 「Name · N 天前」, or the relative part alone when no author is stored. */
  label: string;
  /** Untouched for ≥ TRACKING_NOTE_STALE_DAYS Taipei calendar days → amber stamp. */
  stale: boolean;
}

/**
 * The notes-cell stamp (style sheet § 4.9, PO Q14 = 10 days). `atMs` null = no stamp
 * time stored: the author alone, never stale.
 */
export function noteStamp(
  by: string | null | undefined,
  atMs: number | null,
  nowMs: number,
): NoteStamp {
  const author = (by ?? '').trim();
  if (atMs === null || !Number.isFinite(atMs)) return { label: author, stale: false };
  const relative = formatRelativeDays(atMs, nowMs);
  return {
    label: author ? `${author} · ${relative}` : relative,
    stale: taipeiDaysSince(atMs, nowMs) >= SESSION_ENROLLMENT_CONFIG.TRACKING_NOTE_STALE_DAYS,
  };
}
